import React, { useState, useRef, useEffect } from 'react';
import { Bell, X } from 'lucide-react';
import { useNotifications } from 'src/contexts/NotificationContext';
import { useTimezoneFormatting } from 'src/hooks/useTimezoneFormatting';

interface NotificationBellProps {
    className?: string;
}

export const NotificationBell: React.FC<NotificationBellProps> = ({ className = '' }) => {
    const { unreadCount, notifications, markAsRead, markAllAsRead, refreshNotifications } = useNotifications();
    const { formatNotification } = useTimezoneFormatting();
    const [isOpen, setIsOpen] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const panelRef = useRef<HTMLDivElement>(null);

    // Close panel when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);


    // Use timezone-aware formatting
    const formatDate = (dateString: string) => {
        return formatNotification(dateString);
    };

    return (
        <div className={`relative ${className}`} ref={panelRef}>
            {/* Bell Icon */}
            <button
                onClick={() => {
                    const opening = !isOpen;
                    setIsOpen(opening);
                    if (opening) {
                        refreshNotifications();
                    }
                    // Only mark as read when opening the panel and there are unread notifications
                    if (opening && unreadCount > 0) {
                        markAllAsRead();
                    }
                }}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                className="notification-bell"
                aria-label="Notifications"
            >
                <svg width="14" height="16" viewBox="0 0 20 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12.9325 19.124C12.7885 19.7951 12.4188 20.3965 11.885 20.8279C11.3512 21.2593 10.6856 21.4947 9.99925 21.4947C9.31291 21.4947 8.64731 21.2593 8.11351 20.8279C7.57971 20.3965 7.20998 19.7951 7.066 19.124C9.016 19.3355 10.9825 19.3355 12.9325 19.124ZM10 0.5C11.4805 0.499987 12.9154 1.01288 14.0604 1.95144C15.2054 2.89 15.9899 4.19626 16.2805 5.648L17.209 10.292C17.3995 11.243 17.866 12.116 18.5515 12.8015L19.2055 13.4555C19.4403 13.6903 19.6079 13.9836 19.691 14.305C19.7741 14.6265 19.7697 14.9643 19.6781 15.2834C19.5866 15.6026 19.4113 15.8914 19.1705 16.1199C18.9296 16.3484 18.632 16.5083 18.3085 16.583L16.1155 17.09C12.0916 18.0203 7.90843 18.0203 3.8845 17.09L1.6915 16.583C1.368 16.5083 1.07038 16.3484 0.829538 16.1199C0.588696 15.8914 0.41341 15.6026 0.321878 15.2834C0.230346 14.9643 0.225904 14.6265 0.309012 14.305C0.392121 13.9836 0.559752 13.6903 0.794502 13.4555L1.45 12.8C2.13568 12.1146 2.60285 11.2413 2.7925 10.2905L3.7225 5.6465C4.01327 4.19554 4.79748 2.89002 5.94186 1.95181C7.08624 1.01361 8.52019 0.500611 10 0.5Z" fill="white" />
                </svg>

                {unreadCount > 0 && (
                    <span className="noti-bell-unread-count">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            {/* Notification Panel: fixed bottom-right */}
            {/* Notification Panel: right-side drawer */}
            {isOpen && (
                <div className="fixed inset-0 z-50 flex justify-end">
                    {/* Backdrop */}
                    <div
                        className="flex-1 bg-black/40"
                        onClick={() => setIsOpen(false)}
                    />

                    {/* Panel */}
                    <div className="w-[420px] max-w-full h-full bg-white shadow-2xl border-l border-gray-200 flex flex-col">
                        {/* Header */}
                        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                            <div className="flex items-center gap-3">
                                <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <rect width="40" height="40" rx="6" fill="#F7F7F7" />
                                    <path d="M22.9325 28.124C22.7885 28.7951 22.4188 29.3965 21.885 29.8279C21.3512 30.2593 20.6856 30.4947 19.9992 30.4947C19.3129 30.4947 18.6473 30.2593 18.1135 29.8279C17.5797 29.3965 17.21 28.7951 17.066 28.124C19.016 28.3355 20.9825 28.3355 22.9325 28.124ZM20 9.5C21.4805 9.49999 22.9154 10.0129 24.0604 10.9514C25.2054 11.89 25.9899 13.1963 26.2805 14.648L27.209 19.292C27.3995 20.243 27.866 21.116 28.5515 21.8015L29.2055 22.4555C29.4402 22.6903 29.6079 22.9836 29.691 23.305C29.7741 23.6265 29.7696 23.9643 29.6781 24.2834C29.5866 24.6026 29.4113 24.8914 29.1705 25.1199C28.9296 25.3484 28.632 25.5083 28.3085 25.583L26.1155 26.09C22.0916 27.0203 17.9084 27.0203 13.8845 26.09L11.6915 25.583C11.368 25.5083 11.0704 25.3484 10.8295 25.1199C10.5887 24.8914 10.4134 24.6026 10.3219 24.2834C10.2303 23.9643 10.2259 23.6265 10.309 23.305C10.3921 22.9836 10.5597 22.6903 10.7945 22.4555L11.45 21.8C12.1357 21.1146 12.6028 20.2413 12.7925 19.2905L13.7225 14.6465C14.0133 13.1955 14.7975 11.89 15.9419 10.9518C17.0862 10.0136 18.5202 9.50061 20 9.5Z" fill="#FEC602" />
                                </svg>

                                <h3 className="grey-800">Notifications</h3>
                            </div>
                            <button
                                aria-label="Close notifications"
                                className="text-gray-400 hover:text-gray-600"
                                onClick={() => setIsOpen(false)}
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Notifications List */}
                        <div className="flex-1 overflow-y-auto">
                            {notifications.length === 0 ? (
                                <div className="p-6 text-center text-gray-500">No notifications yet</div>
                            ) : (
                                notifications.map((notification) => {
                                    return (
                                        <div
                                            key={notification.id}
                                            onClick={() => markAsRead(notification.id)}
                                            className="w-full text-left"
                                        >
                                            <div className="px-6 py-4 border-b border-gray-100 transition-colors flex gap-4">
                                                {/* Icon */}
                                                {/* <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${visuals.bg} ${visuals.ring}`}>
                                                    <img src="/icons/pdfIcon.png" alt="PDF" className="h-6 w-6" />
                                                </div> */}
                                                {/* Content */}
                                                <div className="min-w-0 flex-1">
                                                    <h4 className="mb-1 grey-700">
                                                        {notification.title}
                                                    </h4>
                                                    <p className="mt-1 mb-2 text-sm text-gray-600 leading-5">
                                                        {notification.message}
                                                    </p>
                                                    <span className="block mt-1 text-xs text-gray-400">
                                                        {formatDate(notification.createdAt)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default NotificationBell;
